import { useState } from "react";
import moment from "moment";
import { useUserPageContextValueHook } from "@/contexts/UserPageContextProvider";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { useApiKeyQuery, useIssueApiKeyMutation, useRevokeApiKeyMutation } from "@/hooks/ReactQuery/OpenApiReactQuery/OpenApiReactQuery";

const MAIN_API_HOST = process.env.REACT_APP_MAIN_API_HOST;

function formatDateTime(value) {
    return value ? moment(value).format('YYYY.MM.DD HH:mm') : '-';
}

export function FdApiSetting(props) {
    const userPageId = useUserPageContextValueHook()?.userPage?.id;

    const apiKeyQuery = useApiKeyQuery({ userPageId });
    const issueApiKeyMutation = useIssueApiKeyMutation();
    const revokeApiKeyMutation = useRevokeApiKeyMutation();

    // 발급 직후에만 보여주는 원문 키 (새로고침하면 사라짐)
    const [issuedApiKey, setIssuedApiKey] = useState(null);

    const apiKey = apiKeyQuery?.data;

    const handleIssue = () => {
        if (apiKey && !window.confirm('재발급하면 기존 API 키는 즉시 사용할 수 없습니다. 재발급할까요?')) {
            return;
        }

        issueApiKeyMutation.mutate({ body: { userPageId } }, {
            onSuccess: (data) => setIssuedApiKey(data?.apiKey),
        });
    }

    const handleRevoke = () => {
        if (!window.confirm('API 키를 폐기하면 해당 키로 더 이상 호출할 수 없습니다. 폐기할까요?')) {
            return;
        }

        revokeApiKeyMutation.mutate({ body: { userPageId } }, {
            onSuccess: () => setIssuedApiKey(null),
        });
    }

    const handleCopy = async () => {
        await navigator.clipboard.writeText(issuedApiKey);
        customToast.success('API 키가 복사되었습니다.');
    }

    return (
        <div className="flex flex-col gap-5">
            <section className="rounded-[15px] shadow-[var(--defaultBoxShadow)] p-5 flex flex-col gap-4">
                <div>
                    <h2 className="text-lg font-bold">API 키</h2>
                    <p className="text-sm text-gray-500 mt-1">API 키로 내 페이지에 링크를 추가할 수 있습니다. 키는 외부에 노출되지 않도록 주의해 주세요.</p>
                </div>

                {issuedApiKey &&
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col gap-2">
                        <p className="text-sm font-semibold text-amber-700">이 키는 다시 볼 수 없습니다. 지금 복사해서 안전한 곳에 보관해 주세요.</p>
                        <div className="flex gap-2">
                            <code className="flex-1 break-all rounded bg-white border px-3 py-2 text-sm">{issuedApiKey}</code>
                            <button type="button" onClick={handleCopy} className="shrink-0 rounded bg-gray-900 px-4 text-sm text-white">복사</button>
                        </div>
                    </div>
                }

                {apiKeyQuery?.isLoading && <p className="text-sm text-gray-500">불러오는 중...</p>}

                {!apiKeyQuery?.isLoading && !apiKey &&
                    <button type="button" onClick={handleIssue} className="self-start rounded bg-gray-900 px-4 py-2 text-sm text-white">API 키 발급</button>
                }

                {apiKey &&
                    <div className="flex flex-col gap-3">
                        <dl className="grid grid-cols-[100px_1fr] gap-y-1 text-sm">
                            <dt className="text-gray-500">키</dt>
                            <dd className="font-mono">{apiKey.keyPrefix}••••••••</dd>
                            <dt className="text-gray-500">발급일</dt>
                            <dd>{formatDateTime(apiKey.createdAt)}</dd>
                            <dt className="text-gray-500">마지막 사용</dt>
                            <dd>{formatDateTime(apiKey.lastUsedAt)}</dd>
                        </dl>
                        <div className="flex gap-2">
                            <button type="button" onClick={handleIssue} className="rounded border border-gray-300 px-4 py-2 text-sm">재발급</button>
                            <button type="button" onClick={handleRevoke} className="rounded border border-red-300 px-4 py-2 text-sm text-red-600">폐기</button>
                        </div>
                    </div>
                }
            </section>

            <section className="rounded-[15px] shadow-[var(--defaultBoxShadow)] p-5 flex flex-col gap-3">
                <h2 className="text-lg font-bold">사용 방법</h2>
                <ul className="text-sm text-gray-600 list-disc pl-5 flex flex-col gap-1">
                    <li>헤더에 <code>Authorization: Bearer &lt;API 키&gt;</code>를 넣어 호출합니다.</li>
                    <li>분당 100회까지 호출할 수 있고, 페이지당 링크는 최대 1000개입니다.</li>
                    <li>링크 목록 <code>GET /links</code>, 만들기 <code>POST /links</code>, 수정 <code>PATCH /links/&#123;id&#125;</code>, 삭제 <code>DELETE /links/&#123;id&#125;</code>, 일괄 반영 <code>PUT /links/bulk</code>, 클릭 통계 <code>GET /links/&#123;id&#125;/stats</code>, 카테고리 목록 <code>GET /categories</code></li>
                    <li>만들기 필수 값: <code>external_id</code>(내 쪽 고유 키), <code>title</code>(1~50자), <code>url</code>. 선택: <code>description</code>, <code>thumbnail_url</code>, <code>category_id</code>, <code>disclosure</code>(고지 문구), <code>visible</code>, <code>visible_from</code>, <code>visible_until</code></li>
                </ul>
                <pre className="overflow-x-auto rounded bg-gray-900 p-4 text-xs text-gray-100">{`curl -X POST ${MAIN_API_HOST}/v2/public/open-api/links \\
  -H "Authorization: Bearer <API 키>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_id": "261010_post:product:1",
    "title": "관련용품 - 상품명",
    "url": "https://example.com",
    "disclosure": "이 포스팅은 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.",
    "visible_from": "2026-10-10T10:00:00+09:00"
  }'`}</pre>
            </section>
        </div>
    );
}
